import React from "react";
import { useStoreState } from "ezStore";
import { FormGroup } from "@cofano/cck";
import { Grid2 as Grid, Typography } from "@mui/material";
import { Language, hasOptionalFieldPermission, Fields, DataFormat, hasPluginPermission } from "imports/Utils";
import { ShipFromQuery } from "view/ships/ShipDetails";
import CharterSubtypeTag from "view/charters/components/tags/CharterSubtypeTag";
import { RelationLink } from "utils/LinkUtils";

const ShipGeneralDetails = ({ ship }: { ship: ShipFromQuery }) => {
	const session = useStoreState(store => store.session);

	return (
		<Grid container spacing={3}>
			{ship.shipManager && (
				<Grid size={{ lg: 6, md: 6, xs: 12 }}>
					<Typography
						variant="h4"
						fontWeight="medium"
						fontSize="18px"
						color="text.primary"
						mb={2}>
						{Language("SHIP_MANAGER")}
					</Typography>

					<RelationSummary relation={ship.shipManager} />
				</Grid>
			)}

			{ship.skipper && (
				<Grid size={{ lg: 6, md: 6, xs: 12 }}>
					<Typography
						variant="h4"
						fontWeight="medium"
						fontSize="18px"
						color="text.primary"
						mb={2}>
						{Language("SKIPPER")}
					</Typography>

					<RelationSummary relation={ship.skipper} />
				</Grid>
			)}

			{ship.operator && (
				<Grid size={{ lg: 6, md: 6, xs: 12 }}>
					<Typography
						variant="h4"
						fontWeight="medium"
						fontSize="18px"
						color="text.primary"
						mb={2}>
						{Language("OPERATOR")}
					</Typography>

					<RelationSummary relation={ship.operator} />
				</Grid>
			)}

			<Grid size={{ lg: 6, md: 6, xs: 12 }}>
				<Typography
					variant="h4"
					fontWeight="medium"
					fontSize="18px"
					color="text.primary"
					mb={2}>
					{Language("GENERAL")}
				</Typography>

				<FormGroup label={Language("RELATION_SHIP_OWNER")} labelPlacement="left-center" overflow="hidden">
					<RelationLink relation={ship.owner} />
				</FormGroup>

				{hasOptionalFieldPermission(session, Fields.SHIP, "coupledShip") && (
					<FormGroup label={Language("COUPLED_SHIP")} labelPlacement="left-center" overflow="hidden">
						{ship.coupledShip?.name ?? "-"}
					</FormGroup>
				)}

				{hasOptionalFieldPermission(session, Fields.SHIP, "defaultCharterType") && (
					<FormGroup label={Language("CHARTER_TYPE")} labelPlacement="left-center" overflow="hidden">
						{ship.defaultCharterSubtype && (
							<CharterSubtypeTag type={ship.defaultCharterSubtype} />
						)}
					</FormGroup>
				)}

				<FormGroup label={Language("SHIPS_RETRIEVE_ACTUALS")} labelPlacement="left-center" overflow="hidden">
					{DataFormat.boolean(ship.retrieveActuals)}
				</FormGroup>

				{hasPluginPermission(session, "showLastShippedProducts") && (
					<FormGroup label={Language("SHIP_SHOW_LAST_SHIPPED_PRODUCTS")} labelPlacement="left-center" overflow="visible">
						{DataFormat.boolean(ship.showLastProducts)}
					</FormGroup>
				)}

				{hasOptionalFieldPermission(session, Fields.SHIP, "tracking") && (
					<FormGroup label={Language("SHIP_TRACKING")} labelPlacement="left-center" overflow="hidden">
						{DataFormat.boolean(ship.tracking)}
					</FormGroup>
				)}

				{hasOptionalFieldPermission(session, Fields.SHIP, "advancedTracking") && (
					<FormGroup label={Language("SHIPS_ADVANCED_TRACKING")} labelPlacement="left-center" overflow="hidden">
						{DataFormat.boolean(ship.advancedTracking)}
					</FormGroup>
				)}
			</Grid>
		</Grid>
	);
};

const RelationSummary = ({ relation }: { relation: NonNullable<ShipFromQuery["operator"]> }) => {

	const session = useStoreState(store => store.session);

	return (
		<React.Fragment>
			{hasOptionalFieldPermission(session, Fields.RELATION, "customerNumber") && (
				<FormGroup label={Language("CLIENT_NUMBER")} labelPlacement="left-center" overflow="hidden">
					{relation.customerNumber || "-"}
				</FormGroup>
			)}

			<FormGroup label={Language("CLIENT_CODE")} labelPlacement="left-center" overflow="hidden">
				<RelationLink relation={relation} />
			</FormGroup>

			<FormGroup label={Language("OPERATORS_EMAIL")} labelPlacement="left-center" overflow="hidden">
				{relation.email || "-"}
			</FormGroup>

			{hasOptionalFieldPermission(session, Fields.ADDRESS, "town") && (
				<FormGroup label={Language("CLIENT_CITY")} labelPlacement="left-center" overflow="hidden">
					{relation.address?.town || "-"}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.ADDRESS, "street") && (
				<FormGroup label={Language("CLIENT_STREET")} labelPlacement="left-center" overflow="hidden">
					{relation.address?.street || "-"}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.ADDRESS, "zipcode") && (
				<FormGroup label={Language("CLIENT_ZIPCODE")} labelPlacement="left-center" overflow="hidden">
					{relation.address?.zipCode || "-"}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.ADDRESS, "name1") && (
				<FormGroup label={Language("CLIENT_NAME_ONE")} labelPlacement="left-center" overflow="hidden">
					{relation.address?.name1 || "-"}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.ADDRESS, "name2") && (
				<FormGroup label={Language("CLIENT_NAME_TWO")} labelPlacement="left-center" overflow="hidden">
					{relation.address?.name2 || "-"}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.ADDRESS, "country") && (
				<FormGroup label={Language("CLIENT_COUNTRY")} labelPlacement="left-center" overflow="hidden">
					{relation.address?.country || "-"}
				</FormGroup>
			)}
		</React.Fragment>
	);
};

export default ShipGeneralDetails;
